import { greeting } from './greeting';

/**
 * The brand name (#395): what shoppers see as "Made by …" when they scan a
 * product. Mirrors `backend/src/utils/brand-name.ts`, which has the final say.
 */

export const BRAND_NAME_MAX_LENGTH = 60;

// Letters and marks in any script, digits, single spaces and common punctuation.
const ALLOWED = /^[\p{L}\p{M}\p{Nd} &'’.,\-()+/:]+$/u;
const LETTER_OR_DIGIT = /[\p{L}\p{Nd}]/u;

/** NFC, trimmed, runs of spaces collapsed to one. */
export function normalizeBrandName(raw: string): string {
  return raw.normalize('NFC').trim().replace(/ {2,}/g, ' ');
}

/** Why `name` (already normalized) is not a valid brand name, or null when it is. */
export function brandNameProblem(name: string): string | null {
  if (name.length === 0) return 'Enter your brand name.';
  if (name.length > BRAND_NAME_MAX_LENGTH) {
    return `Keep your brand name to ${BRAND_NAME_MAX_LENGTH} characters or fewer.`;
  }
  if (!ALLOWED.test(name)) {
    return "Use letters, numbers, spaces and & ' . , - ( ) + / : only.";
  }
  if (!LETTER_OR_DIGIT.test(name)) return 'Your brand name needs at least one letter or number.';
  if (name.includes('://')) return "A brand name can't be a web address.";
  return null;
}

/** The profile fields the brand-name helpers read. */
export interface BrandProfileFields {
  email?: string;
  fullName?: string;
  displayName?: string;
}

/** The part of an email before the @: the sign-up fallback for fullName. */
export function emailLocalPart(email?: string): string {
  return email?.split('@')[0] ?? '';
}

/**
 * The brand name the account has set, or undefined: displayName, else fullName
 * unless it is just the email local part (what sign-up stores when no name was
 * given), which is not a brand name.
 */
export function brandNameOf(profile?: BrandProfileFields | null): string | undefined {
  const displayName = profile?.displayName?.trim();
  if (displayName) return displayName;
  const fullName = profile?.fullName?.trim();
  if (!fullName || fullName === emailLocalPart(profile?.email)) return undefined;
  return fullName;
}

/** What shoppers see today: the public pages' `displayName || fullName` fallback. */
export function publicNameOf(profile?: BrandProfileFields | null): string {
  return profile?.displayName || profile?.fullName || 'Unknown Creator';
}

/**
 * Whether to nudge the account to add a brand name: an owner (not a team
 * member, not a vault account) with no displayName and no real fullName.
 */
export function needsBrandName(
  profile?: (BrandProfileFields & { isSubUser?: boolean; accountType?: string }) | null
): boolean {
  if (!profile || profile.isSubUser) return false;
  if ((profile.accountType ?? 'client') !== 'client') return false;
  return !brandNameOf(profile);
}

/**
 * The dashboard heading (#395, #410): "Welcome 👋" until the account has a
 * collection, then the time of day ("Good evening 🌙"), also while that is
 * unknown. Named after the brand, never after the email prefix.
 */
export function dashboardGreeting(
  profile: BrandProfileFields | null | undefined,
  hasCollections: boolean | null,
  now = new Date()
): { text: string; emoji: string } {
  const hello = hasCollections === false ? { text: 'Welcome', emoji: '👋' } : greeting(now);
  const name = brandNameOf(profile);
  return { text: name ? `${hello.text}, ${name}` : hello.text, emoji: hello.emoji };
}
